import type { Ecriture } from '@shared/types/Ecriture';
import type { ExerciceFiscal } from '@shared/types/ExerciceFiscal';
import { EtatsFinanciersService } from '@domain/comptabilite/EtatsFinanciersService';
import { ProvisionsOHADAService } from '@domain/comptabilite/ProvisionsOHADA';
import { ReserveLegaleService } from '@domain/gouvernance/ReserveLegale';
import { uuid } from '@shared/utils/uuid';
import { roundFCFA } from '@shared/utils/round';
import { assertExerciceOuvert } from './ExerciceOuvertGuard';

export {
  AucunExerciceOuvertError,
  assertExerciceOuvert,
  isExerciceOuvert,
  requireExerciceOuvert,
} from './ExerciceOuvertGuard';

export interface ClotureParams {
  encours_clients: number;
  masse_salariale: number;
  capital_social: number;
  reserves_existantes: number;
}

export interface ClotureResult {
  exercice: ExerciceFiscal;
  ecritures_provisions: Ecriture[];
  ecritures_resultat: Ecriture[];
  resultat_avant_impot: number;
  resultat_net: number;
  dotation_reserve_legale: number;
}

/**
 * Service orchestrant la clôture annuelle d'un exercice OHADA.
 * Étapes :
 * 1. Génère les provisions de fin d'exercice (créances, congés, IFC)
 * 2. Calcule le résultat avant impôt depuis le CR
 * 3. Détermine la dotation à la réserve légale (10 %, plafond 20 % capital)
 * 4. Génère l'écriture de résultat (compte 1300 / 1190)
 * 5. Passe l'exercice en statut "cloture"
 */
export class ClotureAnnuelleService {
  static cloturer(
    exercice: ExerciceFiscal | null | undefined,
    ecritures_courantes: readonly Ecriture[],
    params: ClotureParams,
  ): ClotureResult {
    assertExerciceOuvert(exercice);
    const annee = exercice.code;

    // 1) Provisions
    const provisions = ProvisionsOHADAService.genererEcritures(exercice.tenant_id, {
      annee,
      encours_clients: params.encours_clients,
      masse_salariale: params.masse_salariale,
    });

    // 2) Compte de résultat avec les provisions incluses
    const ecritures_avec_prov = [...ecritures_courantes, ...provisions.ecritures];
    const cr = EtatsFinanciersService.calculerCompteResultat(ecritures_avec_prov);
    const resultat_net = roundFCFA(cr.resultat_net);

    // 3) Dotation réserve légale
    const dotation = ReserveLegaleService.calculerDotation(
      resultat_net,
      params.capital_social,
      params.reserves_existantes,
    );

    // 4) Écritures de résultat
    const dateEcr = `31/12/${annee}`;
    const periode = `${annee}-12`;
    const ecritures_resultat: Ecriture[] = [];
    if (resultat_net > 0) {
      ecritures_resultat.push(
        this.ecr(
          exercice.tenant_id,
          dateEcr,
          periode,
          '1300',
          resultat_net,
          0,
          'Résultat de l exercice',
        ),
      );
      ecritures_resultat.push(
        this.ecr(
          exercice.tenant_id,
          dateEcr,
          periode,
          '1100',
          0,
          resultat_net,
          'Affectation report à nouveau',
        ),
      );
      if (dotation > 0) {
        ecritures_resultat.push(
          this.ecr(
            exercice.tenant_id,
            dateEcr,
            periode,
            '1100',
            dotation,
            0,
            'Dotation réserve légale (AUSCG)',
          ),
        );
        ecritures_resultat.push(
          this.ecr(
            exercice.tenant_id,
            dateEcr,
            periode,
            '1110',
            0,
            dotation,
            'Dotation réserve légale (AUSCG)',
          ),
        );
      }
    } else if (resultat_net < 0) {
      ecritures_resultat.push(
        this.ecr(
          exercice.tenant_id,
          dateEcr,
          periode,
          '1190',
          -resultat_net,
          0,
          'Report à nouveau débiteur',
        ),
      );
      ecritures_resultat.push(
        this.ecr(
          exercice.tenant_id,
          dateEcr,
          periode,
          '1390',
          0,
          -resultat_net,
          'Résultat déficitaire',
        ),
      );
    }

    const nouvel_exercice: ExerciceFiscal = {
      ...exercice,
      statut: 'cloture',
      date_cloture_definitive: new Date().toISOString(),
      resultat_net,
      affectation_resultat:
        resultat_net > 0
          ? `RAN ${resultat_net} dont réserve légale ${dotation}`
          : 'Report à nouveau débiteur',
    };

    return {
      exercice: nouvel_exercice,
      ecritures_provisions: provisions.ecritures,
      ecritures_resultat,
      resultat_avant_impot: roundFCFA(
        cr.resultat_exploitation + cr.resultat_financier + cr.produits_HAO - cr.charges_HAO,
      ),
      resultat_net,
      dotation_reserve_legale: dotation,
    };
  }

  private static ecr(
    tenant_id: string,
    date: string,
    periode: string,
    compte: string,
    debit: number,
    credit: number,
    libelle: string,
  ): Ecriture {
    return {
      id: uuid('ecr'),
      tenant_id,
      date,
      periode,
      journal: 'OD',
      compte,
      libelle_compte: '',
      libelle,
      debit,
      credit,
      source: 'manuel' as const,
      genere_le: new Date().toISOString(),
    };
  }
}
